/**
 * Shared Abide aiming: hold Will, then wing-and-gap traps.
 * Never stuff a third Prayer onto a row (a packed row crowds adjacent keeps).
 */
import { canPlaceInSlot } from "../game/board/board";
import { LAYER_ORDER, slotsAdjacent, type LayerId } from "../game/board/types";
import type { DragIntent, VerseDef } from "../game/cards/types";
import type { Game } from "../game/Game";

const WINGS = [0, 2] as const;

function prayerCount(game: Game, layer: LayerId): number {
  let n = 0;
  for (const p of game.board[layer].slots) {
    if (p?.side === "player" && p.level > 0) n++;
  }
  return n;
}

function emptySlots(game: Game, layer: LayerId): number[] {
  const out: number[] = [];
  for (let s = 0; s < 3; s++) {
    if (
      canPlaceInSlot(game.board, layer, s, "player") &&
      !game.board[layer].slots[s]
    ) {
      out.push(s);
    }
  }
  return out;
}

function layerOrder(prefer: LayerId, def?: VerseDef): LayerId[] {
  if (def?.lockLayer) return [def.lockLayer];
  const head = def?.preferLayer ?? prefer;
  const out: LayerId[] = [head];
  for (const L of LAYER_ORDER) {
    if (!out.includes(L)) out.push(L);
  }
  // Still honor an explicit "hold Will first" prefer even if the card homes Heart
  if (prefer === "will" && !out.includes("will")) out.unshift("will");
  if (prefer === "will" && out[0] !== "will") {
    return ["will", ...out.filter((L) => L !== "will")];
  }
  return out;
}

function scoreSlot(game: Game, layer: LayerId, slot: number): number {
  let score = 0;
  const slots = game.board[layer].slots;
  const prayers: number[] = [];
  const lies: number[] = [];
  for (let i = 0; i < slots.length; i++) {
    const p = slots[i];
    if (!p || p.level <= 0) continue;
    if (p.side === "player") prayers.push(i);
    else lies.push(i);
  }

  // Trap: occupy the opposite wing if one wing is already ours
  if (prayers.length === 1) {
    const have = prayers[0]!;
    if (have === 0 && slot === 2) score += 8;
    if (have === 2 && slot === 0) score += 8;
    if (have === 1 && (slot === 0 || slot === 2)) score += 4;
  }

  // First keep: Will likes center (covers both wings); outer rows like a wing
  if (prayers.length === 0) {
    if (layer === "will" && slot === 1) score += 6;
    if (layer !== "will" && WINGS.includes(slot as 0 | 2)) score += 5;
    if (layer !== "will" && slot === 1) score += 2;
  }

  // Sit next to a lie so contest can reach it
  for (const ls of lies) {
    if (slotsAdjacent(slot, ls)) score += 5;
    if (slot === ls) score += 2;
  }

  return score;
}

/**
 * Pick an empty legal slot. Skips stuffing a third Prayer on a row.
 */
export function prayerPlaceIntent(
  game: Game,
  prefer: LayerId,
  def?: VerseDef,
): DragIntent | null {
  const order = layerOrder(prefer, def);

  for (const L of order) {
    if (prayerCount(game, L) >= 2) continue;
    const empties = emptySlots(game, L);
    if (!empties.length) continue;
    let best = empties[0]!;
    let bestScore = -999;
    for (const s of empties) {
      const sc = scoreSlot(game, L, s);
      if (sc > bestScore) {
        bestScore = sc;
        best = s;
      }
    }
    return { layer: L, slot: best };
  }

  // No empty trap/hold slot — replace on preferred layer only if we must
  for (const L of order) {
    for (const s of [1, 0, 2]) {
      if (canPlaceInSlot(game.board, L, s, "player") && game.board[L].slots[s]) {
        return { layer: L, slot: s };
      }
    }
  }
  return null;
}
